import {
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import React from "react";
import { User, UserStatus } from "../../../api/usersApi";
import { DataTable, makeColumnsSearchable } from "../../../components/common/DataTable/DataTable";
import { UserRoles } from "../../../constants/roles";
import { getRoleDisplayName } from "../../../utils/roleDisplayMapper";
import { getUserStatusColor, getUserStatusLabel } from "../../../utils/statusColors";

interface UsersListProps {
  users: User[];
  isLoading: boolean;
  onEditClick: (user: User) => void;
  onDeleteClick: (user: User) => void;
  onArchiveClick: (user: User) => void;
  onRestoreClick: (user: User) => void;
  onHistoryClick: (user: User) => void;
  isAdminOrSuperAdmin: boolean;
  title?: string;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  readOnly?: boolean;
  
  // Search functionality
  enableSearch?: boolean;
  showRoleFilter?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const UsersList: React.FC<UsersListProps> = ({
  users,
  isLoading,
  onEditClick,
  onDeleteClick,
  onArchiveClick,
  onRestoreClick,
  onHistoryClick,
  isAdminOrSuperAdmin,
  title = "Users",
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  readOnly = false,
  
  // Search functionality
  enableSearch = false,
  showRoleFilter = true,
  onSearchChange,
}) => {
  const columns = [
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { 
      field: "role", 
      headerName: "Role", 
      flex: 1,
      renderCell: (value: string) => getRoleDisplayName(value)
    },
    { 
      field: "status", 
      headerName: "Status", 
      flex: 1,
      renderCell: (value: UserStatus) => (
        <span style={{ 
          color: getUserStatusColor(value),
          fontWeight: 'bold'
        }}>
          {getUserStatusLabel(value)}
        </span>
      )
    },
  ];

  const getActions = (user: User) => {
    if (readOnly) return [];

    const actions = [];
    const isArchived = user.status === UserStatus.Archived;

    if (isAdminOrSuperAdmin && user.role === UserRoles.TRUCK_DRIVER) {
      actions.push({
        icon: <HistoryIcon />,
        label: "History",
        onClick: onHistoryClick,
        color: "primary" as const,
      });
    }
    
    if (user.role !== UserRoles.ADMIN && !isArchived) {
      actions.push({
        icon: <EditIcon />,
        label: "Edit",
        onClick: onEditClick,
        color: "info" as const,
      });
    }
    
    if (user.role !== UserRoles.ADMIN && isArchived) {
      actions.push({
        icon: <UnarchiveIcon />,
        label: "Restore",
        onClick: onRestoreClick,
        color: "success" as const,
      });
    }

    if (user.role !== UserRoles.ADMIN && !isArchived) {
      actions.push({
        icon: <ArchiveIcon />,
        label: "Archive",
        onClick: onArchiveClick,
        color: "warning" as const,
      });
    }

    if (isAdminOrSuperAdmin && user.role !== UserRoles.ADMIN) {
      actions.push({
        icon: <DeleteIcon />,
        label: "Delete",
        onClick: onDeleteClick,
        color: "error" as const,
      });
    }
    
    return actions;
  };

  const roleFilterOptions = [
    UserRoles.SUPER_ADMIN,
    UserRoles.ADMIN,
    UserRoles.DISPATCHER,
    UserRoles.TRUCK_DRIVER,
    UserRoles.FLEET,
    UserRoles.SAFETY,
    UserRoles.RECRUITING,
    UserRoles.ACCOUNTING,
    UserRoles.IFTA_ADMIN,
  ].map((role) => ({
    value: role,
    label: getRoleDisplayName(role),
  }));

  // Define searchable columns for users
  const searchableColumns = [
    ...makeColumnsSearchable(
      columns,
      ['firstName', 'lastName', 'email'],
      {
        firstName: 'string',
        lastName: 'string',
        email: 'string'
      }
    ),
    ...(showRoleFilter
      ? [
          {
            field: "role",
            headerName: "Role",
            searchable: true,
            searchType: "select" as const,
            selectOptions: roleFilterOptions,
          },
        ]
      : []),
  ];

  return (
    <DataTable<User>
      items={users}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      title={title}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      isLoadingRows={isLoadingRows}
      onLoadMore={onLoadMore}
      
      // Search functionality
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
    />
  );
};

export default UsersList;
